import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take } from 'rxjs';
import { getCookie, hasCookie } from '../shared/utils/cookie.util';
import { OPE_AUTH_CSRF } from '../shared/constants/endpoints';
import { CsrfResponse } from '../shared/dto/csrf-response';

const CSRF_COOKIE_NAME = 'XSRF-TOKEN';
const CSRF_HEADER_NAME = 'X-XSRF-TOKEN';
const STATE_CHANGING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

let csrfFetch$: Observable<string | null> | null = null;

function cloneReqWithTokenHeader(
  req: HttpRequest<unknown>,
  token: string | null
): HttpRequest<unknown> {
  return req.clone({
    setHeaders: { [CSRF_HEADER_NAME]: token ?? '' },
  });
}

/**
 * For state-changing requests (POST/PUT/PATCH/DELETE), ensures the CSRF token is sent in X-XSRF-TOKEN.
 * If the XSRF-TOKEN cookie is missing, triggers a single GET to the CSRF endpoint, then continues with the original request.
 */
export const csrfInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  if (!STATE_CHANGING_METHODS.includes(req.method)) {
    return next(req);
  }

  if (hasCookie(CSRF_COOKIE_NAME)) {
    const token = getCookie(CSRF_COOKIE_NAME);
    if (token) {
      return next(cloneReqWithTokenHeader(req, token));
    }
    return next(req);
  }

  const http = inject(HttpClient);
  if (!csrfFetch$) {
    csrfFetch$ = http
      .get<CsrfResponse>(OPE_AUTH_CSRF, { withCredentials: true })
      .pipe(
        take(1),
        map((r) => r.data?.token ?? null),
        catchError(() => of(null)),
        shareReplay(1)
      );
  }

  return csrfFetch$.pipe(
    switchMap((token) => next(cloneReqWithTokenHeader(req, token))),
    finalize(() => {
      csrfFetch$ = null;
    })
  );
};
